import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  FileText,
  GripVertical,
  AlertTriangle,
  Loader2,
  Shield,
  CreditCard,
  Receipt,
  FolderCheck,
  File,
  Database,
  Info,
} from 'lucide-react';
import { intakeService } from '@/services/api/intake';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type {
  SystemDocumentType,
  DocumentCategory,
  ExtractionField,
  ExtractionFieldType,
  DocumentRequirementStatus,
} from '@/types/intake';

const CATEGORY_ICONS: Record<DocumentCategory, React.ElementType> = {
  identification: Shield,
  personal_information: Shield,
  financial: CreditCard,
  income: CreditCard,
  tax: Receipt,
  deductions: Receipt,
  compliance: FolderCheck,
  superannuation: FolderCheck,
  offsets_rebates: FolderCheck,
  other: File,
};

const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  personal_information: 'Personal Information',
  identification: 'Identification',
  income: 'Income',
  financial: 'Financial',
  deductions: 'Deductions',
  tax: 'Tax',
  superannuation: 'Superannuation',
  offsets_rebates: 'Offsets & Rebates',
  compliance: 'Compliance',
  other: 'Other',
};

const STATUS_LABELS: Record<DocumentRequirementStatus, string> = {
  mandatory: 'Mandatory',
  conditional: 'Conditional',
  optional: 'Optional',
};

const STATUS_VARIANTS: Record<DocumentRequirementStatus, 'destructive' | 'default' | 'secondary'> = {
  mandatory: 'destructive',
  conditional: 'default',
  optional: 'secondary',
};

const FIELD_TYPES: { value: ExtractionFieldType; label: string }[] = [
  { value: 'string', label: 'Text' },
  { value: 'integer', label: 'Integer' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'date', label: 'Date' },
  { value: 'currency', label: 'Currency' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'enum', label: 'Dropdown' },
];

interface SystemDocumentTypeFormData {
  name: string;
  description: string;
  helpText: string;
  category: DocumentCategory;
  status: DocumentRequirementStatus;
  isActive: boolean;
  displayOrder: number;
  allowMultiple: boolean;
  chatInputAllowed: boolean;
  templateAvailable: boolean;
  templateUrl: string;
  acceptedMimeTypes: string[];
  maxFileSizeMb: number;
  extractionEnabled: boolean;
  extractionFields: ExtractionField[];
  extractionPrompt: string;
}

const defaultFormData: SystemDocumentTypeFormData = {
  name: '',
  description: '',
  helpText: '',
  category: 'other',
  status: 'optional',
  isActive: true,
  displayOrder: 0,
  allowMultiple: false,
  chatInputAllowed: false,
  templateAvailable: false,
  templateUrl: '',
  acceptedMimeTypes: ['application/pdf'],
  maxFileSizeMb: 10,
  extractionEnabled: true,
  extractionFields: [],
  extractionPrompt: '',
};

export function SystemDocumentTypesPage() {
  const queryClient = useQueryClient();

  // State
  const [editingDocType, setEditingDocType] = useState<SystemDocumentType | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docTypeToDelete, setDocTypeToDelete] = useState<SystemDocumentType | null>(null);

  // Form state
  const [formData, setFormData] = useState<SystemDocumentTypeFormData>(defaultFormData);

  // Extraction fields editor state
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [fieldFormData, setFieldFormData] = useState<ExtractionField>({
    key: '',
    label: '',
    type: 'string',
    required: false,
  });

  // Fetch system document types
  const { data: documentTypesData, isLoading } = useQuery({
    queryKey: ['system-document-types'],
    queryFn: () => intakeService.listSystemDocumentTypes(),
  });

  const documentTypes = documentTypesData?.documentTypes || [];

  // Group by category
  const groupedDocTypes = documentTypes.reduce((acc, docType) => {
    const category = docType.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(docType);
    return acc;
  }, {} as Record<DocumentCategory, SystemDocumentType[]>);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (input: Partial<SystemDocumentType>) => intakeService.createSystemDocumentType(input),
    onSuccess: () => {
      toast.success('System document type created successfully');
      queryClient.invalidateQueries({ queryKey: ['system-document-types'] });
      setCreateDialogOpen(false);
      resetForm();
    },
    onError: () => {
      toast.error('Failed to create system document type');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<SystemDocumentType> }) =>
      intakeService.updateSystemDocumentType(id, input),
    onSuccess: () => {
      toast.success('System document type updated successfully');
      queryClient.invalidateQueries({ queryKey: ['system-document-types'] });
      setEditingDocType(null);
      resetForm();
    },
    onError: () => {
      toast.error('Failed to update system document type');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => intakeService.deleteSystemDocumentType(id),
    onSuccess: () => {
      toast.success('System document type deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['system-document-types'] });
      setDeleteDialogOpen(false);
      setDocTypeToDelete(null);
    },
    onError: () => {
      toast.error('Failed to delete system document type');
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      intakeService.updateSystemDocumentType(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-document-types'] });
      toast.success('Document type updated');
    },
    onError: () => {
      toast.error('Failed to update document type');
    },
  });

  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingFieldIndex(null);
    setFieldFormData({ key: '', label: '', type: 'string', required: false });
  };

  const openEditDialog = (docType: SystemDocumentType) => {
    setEditingDocType(docType);
    setFormData({
      name: docType.name,
      description: docType.description || '',
      helpText: docType.helpText || '',
      category: docType.category,
      status: docType.status,
      isActive: docType.isActive,
      displayOrder: docType.displayOrder,
      allowMultiple: docType.allowMultiple,
      chatInputAllowed: docType.chatInputAllowed,
      templateAvailable: docType.templateAvailable,
      templateUrl: docType.templateUrl || '',
      acceptedMimeTypes: docType.acceptedMimeTypes,
      maxFileSizeMb: docType.maxFileSizeMb,
      extractionEnabled: docType.extractionEnabled,
      extractionFields: docType.extractionFields || [],
      extractionPrompt: docType.extractionPrompt || '',
    });
  };

  // Extraction field management
  const addExtractionField = () => {
    if (!fieldFormData.key || !fieldFormData.label) {
      toast.error('Field key and label are required');
      return;
    }
    if (formData.extractionFields.some(f => f.key === fieldFormData.key)) {
      toast.error('A field with this key already exists');
      return;
    }
    setFormData({
      ...formData,
      extractionFields: [...formData.extractionFields, { ...fieldFormData }],
    });
    setFieldFormData({ key: '', label: '', type: 'string', required: false });
  };

  const updateExtractionField = (index: number) => {
    if (!fieldFormData.key || !fieldFormData.label) {
      toast.error('Field key and label are required');
      return;
    }
    const updated = [...formData.extractionFields];
    updated[index] = { ...fieldFormData };
    setFormData({ ...formData, extractionFields: updated });
    setEditingFieldIndex(null);
    setFieldFormData({ key: '', label: '', type: 'string', required: false });
  };

  const removeExtractionField = (index: number) => {
    setFormData({
      ...formData,
      extractionFields: formData.extractionFields.filter((_, i) => i !== index),
    });
  };

  const startEditingField = (index: number) => {
    setEditingFieldIndex(index);
    setFieldFormData({ ...formData.extractionFields[index] });
  };

  const handleSave = () => {
    const input = {
      ...formData,
      templateUrl: formData.templateUrl || undefined,
      extractionPrompt: formData.extractionPrompt || undefined,
    };

    if (editingDocType) {
      updateMutation.mutate({ id: editingDocType.id, input });
    } else {
      createMutation.mutate(input);
    }
  };

  const getCategoryIcon = (category: DocumentCategory) => {
    return CATEGORY_ICONS[category] || FileText;
  };

  // Count statistics
  const stats = {
    total: documentTypes.length,
    mandatory: documentTypes.filter(d => d.status === 'mandatory').length,
    conditional: documentTypes.filter(d => d.status === 'conditional').length,
    optional: documentTypes.filter(d => d.status === 'optional').length,
    active: documentTypes.filter(d => d.isActive).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Database className="h-6 w-6 text-primary" />
            <h1 className="text-2xl md:text-3xl font-bold">System Document Types</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Manage global default document types that tenants can copy and customize
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Document Type
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Types</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">{stats.mandatory}</div>
            <div className="text-sm text-muted-foreground">Mandatory</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.conditional}</div>
            <div className="text-sm text-muted-foreground">Conditional</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.optional}</div>
            <div className="text-sm text-muted-foreground">Optional</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-muted-foreground">Active</div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium">About System Document Types</p>
            <p className="mt-1">
              These are the global default document types for Australian tax returns. Tenants can copy these
              to their account and customize the extraction fields as needed. Changes here will not affect
              existing tenant document types - only new copies.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Document Types by Category */}
      {isLoading ? (
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2].map((j) => (
                    <Skeleton key={j} className="h-20 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : documentTypes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">No system document types defined</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create global default document types that tenants can use
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add First Document Type
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {(Object.keys(CATEGORY_LABELS) as DocumentCategory[]).map((category) => {
            const docTypes = groupedDocTypes[category];
            if (!docTypes || docTypes.length === 0) return null;

            const CategoryIcon = getCategoryIcon(category);

            return (
              <Card key={category}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <CategoryIcon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-lg">{CATEGORY_LABELS[category]}</CardTitle>
                    <Badge variant="secondary">{docTypes.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {docTypes
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((docType) => (
                        <div
                          key={docType.id}
                          className={cn(
                            'flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors',
                            !docType.isActive && 'opacity-50'
                          )}
                        >
                          <div className="text-muted-foreground cursor-move">
                            <GripVertical className="h-5 w-5" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium">{docType.name}</p>
                              <Badge variant={STATUS_VARIANTS[docType.status]} className="text-xs">
                                {STATUS_LABELS[docType.status]}
                              </Badge>
                              {!docType.isActive && (
                                <Badge variant="outline" className="text-xs">Inactive</Badge>
                              )}
                              {docType.allowMultiple && (
                                <Badge variant="outline" className="text-xs">Multiple</Badge>
                              )}
                              {docType.chatInputAllowed && (
                                <Badge variant="outline" className="text-xs">Chat Input</Badge>
                              )}
                            </div>
                            {docType.description && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                                {docType.description}
                              </p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>Max: {docType.maxFileSizeMb}MB</span>
                              <span>
                                Formats: {docType.acceptedMimeTypes.map(t => t.split('/')[1]?.toUpperCase() || t).join(', ')}
                              </span>
                              {docType.extractionFields && docType.extractionFields.length > 0 && (
                                <span className="text-primary font-medium">
                                  {docType.extractionFields.length} extraction fields
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`active-${docType.id}`} className="text-xs text-muted-foreground">
                                Active
                              </Label>
                              <Switch
                                id={`active-${docType.id}`}
                                checked={docType.isActive}
                                onCheckedChange={(checked) =>
                                  toggleActiveMutation.mutate({ id: docType.id, isActive: checked })
                                }
                              />
                            </div>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(docType)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => {
                                    setDocTypeToDelete(docType);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen || !!editingDocType}
        onOpenChange={(open) => {
          if (!open) {
            setCreateDialogOpen(false);
            setEditingDocType(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDocType ? 'Edit System Document Type' : 'Add System Document Type'}
            </DialogTitle>
            <DialogDescription>
              {editingDocType
                ? 'Update the global default document type settings'
                : 'Define a new global default document type for tenants to use'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., PAYG Payment Summary"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what this document should contain..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="helpText">Help Text (for clients)</Label>
              <Textarea
                id="helpText"
                placeholder="Instructions to help clients understand what to upload..."
                value={formData.helpText}
                onChange={(e) => setFormData({ ...formData, helpText: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v as DocumentCategory })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(CATEGORY_LABELS) as DocumentCategory[]).map((cat) => (
                      <SelectItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Requirement Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v as DocumentRequirementStatus })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(STATUS_LABELS) as DocumentRequirementStatus[]).map((status) => (
                      <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxSize">Max File Size (MB)</Label>
                <Input
                  id="maxSize"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.maxFileSizeMb}
                  onChange={(e) => setFormData({ ...formData, maxFileSizeMb: parseInt(e.target.value) || 10 })}
                />
              </div>
            </div>

            {/* Toggles */}
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="allowMultiple">Allow Multiple</Label>
                  <p className="text-xs text-muted-foreground">Multiple files can be uploaded</p>
                </div>
                <Switch
                  id="allowMultiple"
                  checked={formData.allowMultiple}
                  onCheckedChange={(checked) => setFormData({ ...formData, allowMultiple: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="chatInputAllowed">Chat Input Allowed</Label>
                  <p className="text-xs text-muted-foreground">Data can be provided via chat</p>
                </div>
                <Switch
                  id="chatInputAllowed"
                  checked={formData.chatInputAllowed}
                  onCheckedChange={(checked) => setFormData({ ...formData, chatInputAllowed: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="extractionEnabled">Extraction Enabled</Label>
                  <p className="text-xs text-muted-foreground">AI will extract data fields</p>
                </div>
                <Switch
                  id="extractionEnabled"
                  checked={formData.extractionEnabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, extractionEnabled: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="isActive">Active</Label>
                  <p className="text-xs text-muted-foreground">Available for tenants to copy</p>
                </div>
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Accepted File Formats</Label>
              <div className="flex flex-wrap gap-2">
                {['application/pdf', 'image/jpeg', 'image/png'].map((mime) => {
                  const isSelected = formData.acceptedMimeTypes?.includes(mime);
                  const label = mime.split('/')[1].toUpperCase();
                  return (
                    <Badge
                      key={mime}
                      variant={isSelected ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        const current = formData.acceptedMimeTypes || [];
                        const updated = isSelected
                          ? current.filter(m => m !== mime)
                          : [...current, mime];
                        setFormData({ ...formData, acceptedMimeTypes: updated });
                      }}
                    >
                      {label}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Extraction Fields Section */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>Extraction Fields</Label>
                <Badge variant="secondary">{formData.extractionFields.length} fields</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Define the data fields that should be extracted from this document type
              </p>

              {/* Current Fields List */}
              {formData.extractionFields.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                  {formData.extractionFields.map((field, index) => (
                    <div
                      key={field.key}
                      className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded text-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{field.label}</span>
                          {field.required && (
                            <Badge variant="destructive" className="text-xs">Required</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {field.key} ({field.type}){field.format && ` - ${field.format}`}
                          {field.note && ` | ${field.note}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => startEditingField(index)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => removeExtractionField(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Field Form */}
              <div className="border rounded-md p-3 space-y-3 bg-muted/30">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Field Key *</Label>
                    <Input
                      placeholder="e.g., grossIncome"
                      value={fieldFormData.key}
                      onChange={(e) => setFieldFormData({ ...fieldFormData, key: e.target.value.replace(/\s/g, '') })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Display Label *</Label>
                    <Input
                      placeholder="e.g., Gross Income"
                      value={fieldFormData.label}
                      onChange={(e) => setFieldFormData({ ...fieldFormData, label: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select
                      value={fieldFormData.type}
                      onValueChange={(v) => setFieldFormData({ ...fieldFormData, type: v as ExtractionFieldType })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map((ft) => (
                          <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Format</Label>
                    <Input
                      placeholder="e.g., DD/MM/YYYY"
                      value={fieldFormData.format || ''}
                      onChange={(e) => setFieldFormData({ ...fieldFormData, format: e.target.value || undefined })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex items-center gap-1">
                      <Switch
                        id="field-required"
                        checked={fieldFormData.required}
                        onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, required: checked })}
                      />
                      <Label htmlFor="field-required" className="text-xs">Required</Label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Note (optional)</Label>
                  <Input
                    placeholder="Additional context about this field..."
                    value={fieldFormData.note || ''}
                    onChange={(e) => setFieldFormData({ ...fieldFormData, note: e.target.value || undefined })}
                    className="h-8 text-sm"
                  />
                </div>
                {fieldFormData.type === 'enum' && (
                  <div>
                    <Label className="text-xs">Options (comma-separated)</Label>
                    <Input
                      placeholder="Option 1, Option 2, Option 3"
                      value={fieldFormData.options?.join(', ') || ''}
                      onChange={(e) => setFieldFormData({
                        ...fieldFormData,
                        options: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                      })}
                      className="h-8 text-sm"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => editingFieldIndex !== null ? updateExtractionField(editingFieldIndex) : addExtractionField()}
                    disabled={!fieldFormData.key || !fieldFormData.label}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {editingFieldIndex !== null ? 'Update Field' : 'Add Field'}
                  </Button>
                  {editingFieldIndex !== null && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingFieldIndex(null);
                        setFieldFormData({ key: '', label: '', type: 'string', required: false });
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Extraction Prompt */}
            {formData.extractionEnabled && (
              <div className="space-y-2">
                <Label htmlFor="extractionPrompt">Custom Extraction Prompt (optional)</Label>
                <Textarea
                  id="extractionPrompt"
                  placeholder="Custom instructions for AI extraction..."
                  value={formData.extractionPrompt}
                  onChange={(e) => setFormData({ ...formData, extractionPrompt: e.target.value })}
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use the default extraction prompt based on the fields above
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                setEditingDocType(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editingDocType ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete System Document Type
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{docTypeToDelete?.name}</strong>?
              This will not affect existing tenant document types that were copied from this template.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => docTypeToDelete && deleteMutation.mutate(docTypeToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
